import React from 'react';
// Converts plain URLs in text to clickable anchor tags
function linkify(text: string): string {
  // Regex to match URLs (http, https, www)
  const urlPattern = /((https?:\/\/|www\.)[\w\-._~:/?#@!$&'()*+,;=%]+)(?![^<]*>|[^[]*])/gi;
  return text.replace(urlPattern, (url) => {
    let href = url;
    if (!href.startsWith('http')) {
      href = 'https://' + href;
    }
    // Add Tailwind blue color class
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline hover:text-blue-800">${url}</a>`;
  });
}
import { cn } from '@/lib/utils';
import DOMPurify from 'dompurify';

interface HTMLRendererProps {
  content: string;
  className?: string;
}

export const HTMLRenderer: React.FC<HTMLRendererProps> = ({
  content,
  className,
}) => {
  // Convert URLs to links before sanitizing
  const processedContent = React.useMemo(() => linkify(content), [content]);
  // Create a custom DOMPurify instance with CSS-friendly configuration
  const sanitizedContent = React.useMemo(() => {
    DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
      if (data.attrName === 'style') {
        const dangerousPatterns = [
          /expression\s*\(/i,
          /javascript:/i,
          /data:/i,
          /vbscript:/i,
          /behavior\s*:/i,
          /-moz-binding/i
        ];
        const hasDangerousContent = dangerousPatterns.some(pattern => 
          pattern.test(data.attrValue)
        );
        if (hasDangerousContent) {
          data.keepAttr = false;
        }
      }
      if (data.attrName === 'src' && node.tagName === 'IMG') {
        if (data.attrValue.startsWith('data:image/')) {
          data.keepAttr = true;
        }
      }
    });
    const result = DOMPurify.sanitize(processedContent, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'a', 'code', 'pre', 'div', 'span', 'img'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class', 'id', 'src', 'alt', 'width', 'height'],
      ALLOW_DATA_ATTR: false,
      ALLOW_UNKNOWN_PROTOCOLS: false,
      ADD_TAGS: ['img'],
      ADD_ATTR: ['src', 'alt', 'width', 'height'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
      FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input']
    });
    DOMPurify.removeHook('uponSanitizeAttribute');
    return result;
  }, [processedContent]);
  return (
    <div 
      className={cn(
        "prose max-w-none",
        // Paragraphs
        "[&_p]:text-base [&_p]:leading-relaxed [&_p]:mb-6 [&_p]:text-foreground",
        // Headings
        "[&_h3]:text-2xl [&_h3]:font-bold [&_h3]:mt-12 [&_h3]:mb-6 [&_h3]:text-foreground [&_h3]:leading-tight",
        "[&_h4]:text-xl [&_h4]:font-semibold [&_h4]:mt-8 [&_h4]:mb-4 [&_h4]:text-foreground",
        "[&_h5]:text-lg [&_h5]:font-semibold [&_h5]:mt-6 [&_h5]:mb-3 [&_h5]:text-foreground",
        // Lists
        "[&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mb-6 [&_ul]:space-y-2",
        "[&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:mb-6 [&_ol]:space-y-2",
        "[&_li]:text-base [&_li]:leading-relaxed [&_li]:text-foreground",
        "[&_li>p]:mb-2",
        // Strong/Bold
        "[&_strong]:font-bold [&_strong]:text-foreground",
        // Links
        "[&_a]:text-primary [&_a]:underline [&_a]:hover:text-primary/80",
        // Blockquotes
        "[&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-6",
        // Code
        "[&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono",
        "[&_pre]:bg-muted [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:mb-6",
        // Images
        "[&_img]:rounded-lg [&_img]:my-8 [&_img]:shadow-lg",
        className
      )}
      style={{
        direction: 'ltr',
        textAlign: 'left',
        unicodeBidi: 'plaintext'
      }}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
};

export default HTMLRenderer;